import { requireCompletedProfile } from "@hisani/auth";
import { BillAllocationError, allocateBillAmount, resolveBillAllocation, } from "../domain/accounting/bill-allocation.js";
import { isKnownExpenseCategory } from "../domain/accounting/expense-category.js";
import { createBillSchema, getBillSchema, listBillsSchema, setBillAllocationSchema, stableConflict, stableInvalidTransition, stableNotFound, updateBillSchema, } from "../validation.js";
import { beginSealedRecordDocumentUpload, getSealedRecordDocumentUrl, requireFeatureMembership, requireHorseAcceptingSealedRecords, verifyAndClaimSealedRecordDocuments, } from "./sealed-record-support.js";
const AREA = "accounting-bills";
const FEATURE = "ACCOUNTING";
const HORSE_ALLOCATION_UNAVAILABLE_MESSAGE = "Every allocated horse must belong to this Stable and be accepting new Sealed records.";
async function requireKnownExpenseCategory(dependencies, category) {
    const policy = await dependencies.policy.get();
    if (!isKnownExpenseCategory(policy.expenseCategories, category)) {
        throw stableInvalidTransition("This expense category is not configured for this Stable.");
    }
}
export function beginBillDocumentUpload(principal, input, dependencies) {
    return beginSealedRecordDocumentUpload(principal, input, AREA, FEATURE, dependencies);
}
/**
 * Captures a bill/receipt — either fully manually or with the confirmed
 * fields from a `scanBillDocument` draft (`extractionId` links the two for
 * provenance; every field is independently editable regardless of its
 * source, `ARCHITECTURE.md` §11.12). `allocation` is resolved and validated
 * here ("must sum to exactly 100%") before anything is persisted — see
 * `domain/accounting/bill-allocation.ts`.
 */
export async function createBill(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = createBillSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    await requireKnownExpenseCategory(dependencies, parsed.category);
    const shares = resolveAllocationOrThrow(parsed.allocation);
    await Promise.all(shares.map((share) => requireHorseAcceptingSealedRecords(dependencies, parsed.stableId, share.horseId)));
    await verifyAndClaimSealedRecordDocuments(parsed.stableId, AREA, parsed.documents, dependencies);
    const allocations = allocateBillAmount(parsed.amountMinor, shares);
    const now = dependencies.clock.now();
    const result = await dependencies.repository.createBill({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        vendor: parsed.vendor,
        billDate: parsed.billDate,
        description: parsed.description,
        category: parsed.category,
        amountMinor: parsed.amountMinor,
        currency: parsed.currency,
        allocationMode: parsed.allocation.mode,
        allocations,
        documents: parsed.documents,
        extractionId: parsed.extractionId,
        actorId: actor.account.id,
        now,
    });
    if (result.outcome === "HORSE_UNAVAILABLE") {
        throw stableInvalidTransition(HORSE_ALLOCATION_UNAVAILABLE_MESSAGE);
    }
    return result.bill;
}
function resolveAllocationOrThrow(allocation) {
    try {
        return resolveBillAllocation(allocation);
    }
    catch (error) {
        if (error instanceof BillAllocationError) {
            throw stableInvalidTransition(error.message);
        }
        throw error;
    }
}
/** Edits a bill's own fields (vendor/date/description/category/amount/
 * currency, plus appending documents) — allocation is edited separately via
 * {@link setBillAllocation} since it carries its own invariant. */
export async function updateBill(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = updateBillSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    await requireKnownExpenseCategory(dependencies, parsed.category);
    await verifyAndClaimSealedRecordDocuments(parsed.stableId, AREA, parsed.documents, dependencies);
    const existing = await dependencies.repository.getBill(parsed.stableId, parsed.billId);
    if (existing === null)
        throw stableNotFound();
    // Allocation shares/mode are edited only through `setBillAllocation`; an
    // amount edit here re-splits the existing shares against the new amount
    // so the stored per-horse amounts never go stale relative to the bill's
    // own total (`allocateBillAmount`).
    const allocations = allocateBillAmount(parsed.amountMinor, existing.allocations);
    const now = dependencies.clock.now();
    const bill = await dependencies.repository.updateBill({
        stableId: parsed.stableId,
        billId: parsed.billId,
        expectedVersion: parsed.expectedVersion,
        vendor: parsed.vendor,
        billDate: parsed.billDate,
        description: parsed.description,
        category: parsed.category,
        amountMinor: parsed.amountMinor,
        currency: parsed.currency,
        allocations,
        documents: parsed.documents,
        actorId: actor.account.id,
        now,
    });
    if (bill === null)
        throw stableConflict();
    return bill;
}
export async function setBillAllocation(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = setBillAllocationSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const existing = await dependencies.repository.getBill(parsed.stableId, parsed.billId);
    if (existing === null)
        throw stableNotFound();
    const shares = resolveAllocationOrThrow(parsed.allocation);
    await Promise.all(shares.map((share) => requireHorseAcceptingSealedRecords(dependencies, parsed.stableId, share.horseId)));
    const allocations = allocateBillAmount(existing.amountMinor, shares);
    const now = dependencies.clock.now();
    const bill = await dependencies.repository.setBillAllocation({
        stableId: parsed.stableId,
        billId: parsed.billId,
        expectedVersion: parsed.expectedVersion,
        allocationMode: parsed.allocation.mode,
        allocations,
        actorId: actor.account.id,
        now,
    });
    if (bill.outcome === "HORSE_UNAVAILABLE") {
        throw stableInvalidTransition(HORSE_ALLOCATION_UNAVAILABLE_MESSAGE);
    }
    if (bill.outcome === "CONFLICT")
        throw stableConflict();
    return bill.bill;
}
export async function getBill(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = getBillSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "VIEW");
    return dependencies.repository.getBill(parsed.stableId, parsed.billId);
}
export async function listBills(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listBillsSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "VIEW");
    return dependencies.repository.listBills({
        stableId: parsed.stableId,
        ...(parsed.horseId === undefined ? {} : { horseId: parsed.horseId }),
        ...(parsed.category === undefined ? {} : { category: parsed.category }),
        limit: parsed.limit,
        cursor: parsed.cursor,
    });
}
export function getBillDocumentUrl(principal, input, dependencies) {
    return getSealedRecordDocumentUrl(principal, input, FEATURE, (stableId, documentId) => dependencies.repository.getBillDocument(stableId, documentId), dependencies);
}
